import { type Control, type Path } from 'react-hook-form'
import { DateField } from './DateField'
import { StringField } from './StringField'
// import { DateTimeField } from './DateTimeField'
import { useAppPageState } from '../../AppPage2/useAppPageState'
import { ArrayInputField } from './ArrayInputField'
import { BooleanField } from './BooleanField'
import { ObjectListField } from './ObjectListField'
import { CascadingField } from './CascadingField/index'
import { DateTimeField2 } from './DateTimeField2'
import { FileField } from './FileField'
import { MultiSelectComboboxField } from './MultiSelectComboboxField'
import { NumberField } from './NumberField'
import { SingleSelectComboboxField } from './SingleSelectComboboxField'
import { SliderField } from './SliderField'
import { TextAreaField } from './TextAreaField'
import { BaseFieldProps, FormValues, JsonFormPropertyV2 } from './types'
import { UriField } from './UriField'
import { RadioField } from './RadioField'

interface FieldRendererProps {
  field: JsonFormPropertyV2
  control: Control<FormValues>
  variant: BaseFieldProps['variant']
  popoverContentClassName?: string
}

interface RenderFieldProps extends FieldRendererProps {
  type: string
}

/**
 * Wrapper for fields that don't natively support the 'inline' variant.
 * Applies CSS overrides to hide labels, descriptions, documentation, and enforce compact sizing.
 */
function InlineFieldWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-0 [&>div]:space-y-0 [&_button[aria-label]]:hidden [&_button]:h-6 [&_input]:h-6 [&_label]:hidden [&_p]:hidden">
      {children}
    </div>
  )
}

function RenderField({ type, field, control, variant, popoverContentClassName }: RenderFieldProps) {
  const formFieldName = field.id as Path<FormValues>
  switch (type) {
    case 'string':
      if (field.format === 'date') {
        return <DateField field={field} formFieldName={formFieldName} control={control} variant={variant} />
      }

      if (field.format && ['date-time', 'datetime'].includes(field.format)) {
        const dateTimeField = (
          <DateTimeField2 field={field} formFieldName={formFieldName} control={control} variant={variant} />
        )
        return variant === 'inline' ? <InlineFieldWrapper>{dateTimeField}</InlineFieldWrapper> : dateTimeField
      }

      if (field.format === 'file-url') {
        const fileField = <FileField field={field} formFieldName={formFieldName} control={control} />
        return variant === 'inline' ? <InlineFieldWrapper>{fileField}</InlineFieldWrapper> : fileField
      }

      if (field.format === 'uri') {
        const uriField = <UriField field={field} formFieldName={formFieldName} control={control} />
        return variant === 'inline' ? <InlineFieldWrapper>{uriField}</InlineFieldWrapper> : uriField
      }

      if (field.widget === 'textarea') {
        const textAreaField = <TextAreaField field={field} formFieldName={formFieldName} control={control} />
        return variant === 'inline' ? <InlineFieldWrapper>{textAreaField}</InlineFieldWrapper> : textAreaField
      }

      if (field.choices) {
        // if (field.widget === 'radio' && variant !== 'row' && variant !== 'inline') {
        if (field.widget === 'radio' && variant !== 'inline') {
          return <RadioField field={field} formFieldName={formFieldName} control={control} variant={variant} />
        }
        return (
          <SingleSelectComboboxField
            field={field}
            formFieldName={formFieldName}
            control={control}
            variant={variant}
            popoverContentClassName={popoverContentClassName}
            showIcons={field.id === 'icon_name'}
          />
        )
      }

      return <StringField field={field} formFieldName={formFieldName} control={control} variant={variant} />

    case 'integer':
    case 'number':
      if (field.choices) {
        return (
          <SingleSelectComboboxField
            field={field}
            formFieldName={formFieldName}
            control={control}
            variant={variant}
            popoverContentClassName={popoverContentClassName}
            showIcons={field.id === 'icon_name'}
          />
        )
      }
      if (field.widget === 'range') {
        const sliderField = <SliderField field={field} formFieldName={formFieldName} control={control} />
        return variant === 'inline' ? <InlineFieldWrapper>{sliderField}</InlineFieldWrapper> : sliderField
      }

      return <NumberField field={field} formFieldName={formFieldName} control={control} variant={variant} />

    case 'boolean':
      return <BooleanField field={field} formFieldName={formFieldName} control={control} variant={variant} />

    case 'array':
      if ((field.items?.widget === 'multiselect' || field.widget === 'multiselect') && field.items?.choices) {
        return (
          <MultiSelectComboboxField
            field={field}
            formFieldName={formFieldName}
            control={control}
            variant={variant}
            popoverContentClassName={popoverContentClassName}
          />
        )
      }
      if (field.items?.type === 'object' && field.items?.properties) {
        return <ObjectListField field={field} formFieldName={formFieldName} control={control} variant={variant} />
      }
      return <ArrayInputField field={field} formFieldName={formFieldName} control={control} variant={variant} />

    case 'cascading':
      return variant === 'column' ? (
        <div className="flex flex-1 rounded-md bg-muted p-3">
          <CascadingField field={field} formFieldName={formFieldName} control={control} variant={variant} />
        </div>
      ) : (
        <CascadingField field={field} formFieldName={formFieldName} control={control} variant={variant} />
      )

    default:
      return null
  }
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  control,
  variant = 'column',
  popoverContentClassName,
}) => {
  const { isConfiguringLayout } = useAppPageState()

  if (isConfiguringLayout) {
    return (
      <div className="pointer-events-none opacity-50">
        <RenderField
          type={field.type}
          field={field}
          control={control}
          variant={variant}
          popoverContentClassName={popoverContentClassName}
        />
      </div>
    )
  }

  return (
    <RenderField
      type={field.type}
      field={field}
      control={control}
      variant={variant}
      popoverContentClassName={popoverContentClassName}
    />
  )
}
